import { Camera, Controls, MathUtils, Spherical, Vector3 } from "three";
import { CollisionSystem } from "../collision/CollisionSystem";

const _lookDirection = new Vector3();
const _spherical = new Spherical();
const _target = new Vector3();
const _targetPosition = new Vector3();
const DRAG_INERTIA_EPSILON = 0.001;

/**
 * Event map for FirstPersonControls
 */
interface FirstPersonControlsEventMap {
  // Add custom events here if needed
}

/**
 * Custom controls class extending Controls, based on FirstPersonControls
 */
export class FirstPersonControls extends Controls<FirstPersonControlsEventMap> {
  /**
   * The movement speed.
   */
  movementSpeed: number = 1.0;

  /**
   * Scale factor converting pinch distance delta (in px) into world movement.
   */
  pinchMoveSpeed: number = 0.005;

  /**
   * Degrees rotated when dragging across the full viewport width.
   */
  lookSpeedHorizontal: number = 200;

  /**
   * Degrees rotated when dragging across the full viewport height.
   */
  lookSpeedVertical: number = 200;

  /**
   * Fraction of drag velocity preserved per 60fps frame (0 disables inertia).
   */
  dragInertia: number = 0;

  /**
   * Whether it's possible to vertically look around or not.
   */
  lookVertical: boolean = true;

  /**
   * Whether the camera is automatically moved forward or not.
   * Disabled by default - camera should not move unless user presses keys.
   */
  autoForward: boolean = false;

  /**
   * Whether it's possible to look around or not.
   */
  activeLook: boolean = true;

  /**
   * Whether or not the camera's height influences the forward movement speed.
   */
  heightSpeed: boolean = false;

  /**
   * Determines how much faster the camera moves when it's y-component is near `speedHeightMax`.
   */
  heightCoef: number = 1.0;

  /**
   * Lower height limit used for movement speed adjustment (when heightSpeed is enabled).
   * Speed increases as camera moves from speedHeightMin toward speedHeightMax.
   */
  speedHeightMin: number = 0.0;

  /**
   * Upper height limit used for movement speed adjustment (when heightSpeed is enabled).
   * Speed increases as camera moves from speedHeightMin toward speedHeightMax.
   */
  speedHeightMax: number = 1.0;

  /**
   * Minimum camera Y position in world space. Camera cannot move below this height.
   * This is a hard limit that clamps the camera position.
   */
  clampMinHeight: number = -Infinity;

  /**
   * Maximum camera Y position in world space. Camera cannot move above this height.
   * This is a hard limit that clamps the camera position.
   */
  clampMaxHeight: number = Infinity;

  /**
   * Whether or not looking around is vertically constrained by `verticalMin` and `verticalMax`.
   */
  constrainVertical: boolean = false;

  /**
   * How far you can vertically look around, lower limit. Range is `0` to `Math.PI` in radians.
   */
  verticalMin: number = 0;

  /**
   * How far you can vertically look around, upper limit. Range is `0` to `Math.PI` in radians.
   */
  verticalMax: number = Math.PI;

  /**
   * Whether the mouse is pressed down or not.
   */
  mouseDragOn: boolean = false;

  /**
   * Collision system for preventing camera from passing through objects.
   * If null, collision detection is disabled.
   */
  collisionSystem: CollisionSystem | null = null;

  // Internal state
  private _autoSpeedFactor: number = 0.0;
  private _pointerX: number = 0; // Movement delta X
  private _pointerY: number = 0; // Movement delta Y
  private _lastPointerX: number = 0; // Last pointer X position
  private _lastPointerY: number = 0; // Last pointer Y position
  private _moveForward: boolean = false;
  private _moveBackward: boolean = false;
  private _moveLeft: boolean = false;
  private _moveRight: boolean = false;
  private _moveUp: boolean = false;
  private _moveDown: boolean = false;
  private _viewHalfX: number = 0;
  private _viewHalfY: number = 0;
  private _lat: number = 0;
  private _lon: number = 0;
  private _activeTouches: Map<number, { x: number; y: number }> = new Map();
  private _lookTouchId: number | null = null;
  private _isPinching: boolean = false;
  private _lastPinchDistance: number | null = null;
  private _pendingPinchDistance: number = 0;
  private _dragVelocityX: number = 0;
  private _dragVelocityY: number = 0;
  private _scratchCurrentPosition: Vector3 = new Vector3();
  private _scratchMovement: Vector3 = new Vector3();
  private _scratchDesiredPosition: Vector3 = new Vector3();

  // Event handlers
  private _onPointerMove: (event: PointerEvent) => void;
  private _onPointerDown: (event: PointerEvent) => void;
  private _onPointerUp: (event: PointerEvent) => void;
  private _onPointerCancel: (event: PointerEvent) => void;
  private _onPointerLeave: (event: PointerEvent) => void;
  private _onContextMenu: (event: MouseEvent) => void;
  private _onKeyDown: (event: KeyboardEvent) => void;
  private _onKeyUp: (event: KeyboardEvent) => void;

  constructor(object: Camera, domElement: HTMLElement | null = null) {
    super(object, domElement);

    // Bind event handlers
    this._onPointerMove = this.onPointerMove.bind(this);
    this._onPointerDown = this.onPointerDown.bind(this);
    this._onPointerUp = this.onPointerUp.bind(this);
    this._onPointerCancel = this.onPointerCancel.bind(this);
    this._onPointerLeave = this.onPointerLeave.bind(this);
    this._onContextMenu = this.onContextMenu.bind(this);
    this._onKeyDown = this.onKeyDown.bind(this);
    this._onKeyUp = this.onKeyUp.bind(this);
  }

  connect(element: HTMLElement): void {
    super.connect(element);

    window.addEventListener("keydown", this._onKeyDown);
    window.addEventListener("keyup", this._onKeyUp);

    this.domElement!.addEventListener("pointerdown", this._onPointerDown);
    this.domElement!.addEventListener("pointerleave", this._onPointerLeave);
    window.addEventListener("pointermove", this._onPointerMove);
    window.addEventListener("pointerup", this._onPointerUp);
    window.addEventListener("pointercancel", this._onPointerCancel);
    this.domElement!.addEventListener("contextmenu", this._onContextMenu);
  }

  disconnect(): void {
    if (this.domElement) {
      window.removeEventListener("keydown", this._onKeyDown);
      window.removeEventListener("keyup", this._onKeyUp);

      this.domElement.removeEventListener("pointerdown", this._onPointerDown);
      this.domElement.removeEventListener("pointerleave", this._onPointerLeave);
      window.removeEventListener("pointermove", this._onPointerMove);
      window.removeEventListener("pointerup", this._onPointerUp);
      window.removeEventListener("pointercancel", this._onPointerCancel);
      this.domElement.removeEventListener("contextmenu", this._onContextMenu);
    }

    super.disconnect();
  }

  dispose(): void {
    this.disconnect();
  }

  /**
   * Must be called if the application window is resized.
   */
  handleResize(): void {
    if (
      this.domElement === null ||
      this.domElement === document.documentElement ||
      this.domElement === document.body
    ) {
      this._viewHalfX = window.innerWidth / 2;
      this._viewHalfY = window.innerHeight / 2;
    } else {
      this._viewHalfX = this.domElement.offsetWidth / 2;
      this._viewHalfY = this.domElement.offsetHeight / 2;
    }
  }

  /**
   * Rotates the camera towards the defined target position.
   */
  lookAt(x: number | Vector3, y?: number, z?: number): this {
    if (x instanceof Vector3) {
      _target.copy(x);
    } else {
      _target.set(x, y!, z!);
    }

    this.object.lookAt(_target);
    this.syncOrientationFromCamera();

    return this;
  }

  update(delta: number): void {
    if (this.enabled === false) return;

    if (this.heightSpeed) {
      const y = MathUtils.clamp(
        this.object.position.y,
        this.speedHeightMin,
        this.speedHeightMax
      );
      const heightDelta = y - this.speedHeightMin;
      this._autoSpeedFactor = delta * (heightDelta * this.heightCoef);
    } else {
      this._autoSpeedFactor = 0.0;
    }

    const actualMoveSpeed = delta * this.movementSpeed;

    // Store current position for collision checking
    const currentPosition = this._scratchCurrentPosition.copy(this.object.position);

    // Calculate desired movement
    const movement = this._scratchMovement.set(0, 0, 0);

    if (this._moveForward || (this.autoForward && !this._moveBackward)) {
      movement.z -= actualMoveSpeed + this._autoSpeedFactor;
    }
    if (this._moveBackward) {
      movement.z += actualMoveSpeed;
    }

    if (this._moveLeft) {
      movement.x -= actualMoveSpeed;
    }
    if (this._moveRight) {
      movement.x += actualMoveSpeed;
    }

    if (this._moveUp) {
      movement.y += actualMoveSpeed;
    }
    if (this._moveDown) {
      movement.y -= actualMoveSpeed;
    }

    if (this._pendingPinchDistance !== 0) {
      movement.z += this._pendingPinchDistance * this.pinchMoveSpeed;
      this._pendingPinchDistance = 0;
    }

    // Apply movement with collision detection
    if (movement.lengthSq() > 0 && this.collisionSystem) {
      // Transform movement to world space (relative to camera orientation)
      movement.applyQuaternion(this.object.quaternion);
      const desiredPosition = this._scratchDesiredPosition.copy(currentPosition).add(movement);

      // Get safe position that avoids collisions
      const safePosition = this.collisionSystem.getSafePosition(
        currentPosition,
        desiredPosition
      );
      this.object.position.copy(safePosition);
    } else if (movement.lengthSq() > 0) {
      // No collision system, apply movement directly
      movement.applyQuaternion(this.object.quaternion);
      this.object.position.add(movement);
    }

    // Clamp camera Y position to clampMinHeight/clampMaxHeight limit
    if (this.clampMinHeight !== -Infinity || this.clampMaxHeight !== Infinity) {
      this.object.position.y = MathUtils.clamp(
        this.object.position.y,
        this.clampMinHeight,
        this.clampMaxHeight
      );
    }

    // Handle drag-to-look (with optional inertia)
    if (this.activeLook) {
      let verticalLookRatio = 1;

      if (this.constrainVertical) {
        verticalLookRatio = Math.PI / (this.verticalMax - this.verticalMin);
      }

      let deltaX = 0;
      let deltaY = 0;
      let shouldApplyLook = false;

      if (this.mouseDragOn && (this._pointerX !== 0 || this._pointerY !== 0)) {
        deltaX = this._pointerX;
        deltaY = this._pointerY;
        shouldApplyLook = true;

        // Capture latest velocity to seed inertia when drag stops
        this._dragVelocityX = deltaX;
        this._dragVelocityY = deltaY;
      } else if (!this.mouseDragOn && this.dragInertia > 0) {
        const inertiaMagnitude =
          Math.abs(this._dragVelocityX) + Math.abs(this._dragVelocityY);

        if (inertiaMagnitude > DRAG_INERTIA_EPSILON) {
          deltaX = this._dragVelocityX;
          deltaY = this._dragVelocityY;
          shouldApplyLook = true;

          const baseInertia = MathUtils.clamp(this.dragInertia, 0, 0.9999);
          const damping = Math.pow(baseInertia, delta * 60);
          this._dragVelocityX *= damping;
          this._dragVelocityY *= damping;

          if (Math.abs(this._dragVelocityX) < DRAG_INERTIA_EPSILON) {
            this._dragVelocityX = 0;
          }
          if (Math.abs(this._dragVelocityY) < DRAG_INERTIA_EPSILON) {
            this._dragVelocityY = 0;
          }
        }
      }

      if (shouldApplyLook) {
        const viewWidth = this._viewHalfX > 0 ? this._viewHalfX * 2 : 1;
        const viewHeight = this._viewHalfY > 0 ? this._viewHalfY * 2 : 1;
        const normalizedDeltaX = deltaX / viewWidth;
        const normalizedDeltaY = deltaY / viewHeight;

        this._lon += normalizedDeltaX * this.lookSpeedHorizontal;
        if (this.lookVertical) {
          this._lat +=
            normalizedDeltaY * this.lookSpeedVertical * verticalLookRatio;
        }
      }

      // Reset pointer deltas only while dragging
      if (this.mouseDragOn) {
        this._pointerX = 0;
        this._pointerY = 0;
      }
    }

    // Clamp _lat based on vertical constraints (convert from radians to degrees)
    if (this.constrainVertical) {
      const latMin = 90 - MathUtils.radToDeg(this.verticalMax);
      const latMax = 90 - MathUtils.radToDeg(this.verticalMin);
      this._lat = Math.max(latMin, Math.min(latMax, this._lat));
    }

    let phi = MathUtils.degToRad(90 - this._lat);
    const theta = MathUtils.degToRad(this._lon);

    const position = this.object.position;
    _targetPosition.setFromSphericalCoords(1, phi, theta).add(position);
    this.object.lookAt(_targetPosition);
  }

  syncOrientationFromCamera(): void {
    const quaternion = this.object.quaternion;
    _lookDirection.set(0, 0, -1).applyQuaternion(quaternion);
    _spherical.setFromVector3(_lookDirection);

    this._lat = 90 - MathUtils.radToDeg(_spherical.phi);
    this._lon = MathUtils.radToDeg(_spherical.theta);
  }

  resetInputState(): void {
    this.mouseDragOn = false;
    this._moveForward = false;
    this._moveBackward = false;
    this._moveLeft = false;
    this._moveRight = false;
    this._moveUp = false;
    this._moveDown = false;
    this._pointerX = 0;
    this._pointerY = 0;
    this._dragVelocityX = 0;
    this._dragVelocityY = 0;
    this._pendingPinchDistance = 0;
    this._isPinching = false;
    this._lastPinchDistance = null;
    this._activeTouches.clear();
    this._lookTouchId = null;
  }

  private isFirstPersonInputAllowed(): boolean {
    return this.enabled;
  }

  private isKeyboardSceneControlAllowed(): boolean {
    if (!this.isFirstPersonInputAllowed()) {
      return false;
    }

    const activeElement = document.activeElement as HTMLElement | null;
    if (!activeElement) {
      return true;
    }

    if (
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement ||
      activeElement.isContentEditable
    ) {
      return false;
    }

    return true;
  }

  private onPointerDown(event: PointerEvent): void {
    if (!this.isFirstPersonInputAllowed()) {
      return;
    }
    if (
      this.domElement !== null &&
      this.domElement !== document.documentElement &&
      this.domElement !== document.body
    ) {
      this.domElement.focus();
    }

    if (event.pointerType === "touch") {
      this.handleTouchStart(event);
      return;
    }

    // Only enable drag-to-look on left mouse button (button 0)
    // Right mouse button (button 2) is reserved for context menu
    if (event.button === 0) {
      this.mouseDragOn = true;
      // Capture initial pointer position
      if (
        this.domElement === null ||
        this.domElement === document.documentElement ||
        this.domElement === document.body
      ) {
        this._lastPointerX = event.pageX;
        this._lastPointerY = event.pageY;
      } else {
        this._lastPointerX = event.pageX - this.domElement.offsetLeft;
        this._lastPointerY = event.pageY - this.domElement.offsetTop;
      }
      // Reset movement delta
      this._pointerX = 0;
      this._pointerY = 0;
      this._dragVelocityX = 0;
      this._dragVelocityY = 0;
    }
  }

  private onPointerUp(event: PointerEvent): void {
    if (!this.isFirstPersonInputAllowed()) {
      return;
    }
    if (event.pointerType === "touch") {
      this.handleTouchEnd(event);
      return;
    }

    // Stop dragging when left mouse button is released
    if (event.button === 0) {
      this.mouseDragOn = false;
      // Reset pointer position
      this._pointerX = 0;
      this._pointerY = 0;
    }
  }

  private onPointerLeave(event: PointerEvent): void {
    if (!this.isFirstPersonInputAllowed()) {
      return;
    }
    if (event.pointerType === "touch") {
      if (this._activeTouches.has(event.pointerId)) {
        this.handleTouchEnd(event);
      }
      return;
    }

    if (!this.mouseDragOn) {
      return;
    }

    this.mouseDragOn = false;
    this._pointerX = 0;
    this._pointerY = 0;
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.isFirstPersonInputAllowed()) {
      return;
    }
    if (event.pointerType === "touch") {
      this.handleTouchMove(event);
      return;
    }

    // Only track pointer movement when dragging
    if (!this.mouseDragOn) {
      return;
    }

    // Calculate current pointer position
    let currentX: number;
    let currentY: number;

    if (
      this.domElement === null ||
      this.domElement === document.documentElement ||
      this.domElement === document.body
    ) {
      currentX = event.pageX;
      currentY = event.pageY;
    } else {
      currentX = event.pageX - this.domElement.offsetLeft;
      currentY = event.pageY - this.domElement.offsetTop;
    }

    // Calculate movement delta from last position
    const deltaX = currentX - this._lastPointerX;
    const deltaY = currentY - this._lastPointerY;
    // Accumulate deltas in case multiple pointer events fire between frames
    this._pointerX += deltaX;
    this._pointerY += deltaY;

    // Update last position for next frame
    this._lastPointerX = currentX;
    this._lastPointerY = currentY;
  }

  private onPointerCancel(event: PointerEvent): void {
    if (!this.isFirstPersonInputAllowed()) {
      return;
    }
    if (event.pointerType === "touch") {
      this.handleTouchEnd(event);
    }
  }

  private handleTouchStart(event: PointerEvent): void {
    if (!this.isFirstPersonInputAllowed()) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }

    const position = this._getRelativePointerPosition(event);
    this._activeTouches.set(event.pointerId, position);

    if (this._activeTouches.size === 1) {
      this._lookTouchId = event.pointerId;
      this.mouseDragOn = true;
      this._lastPointerX = position.x;
      this._lastPointerY = position.y;
      this._pointerX = 0;
      this._pointerY = 0;
      this._dragVelocityX = 0;
      this._dragVelocityY = 0;
      return;
    }

    if (this._activeTouches.size >= 2) {
      this._startPinch();
    }
  }

  private handleTouchMove(event: PointerEvent): void {
    if (!this.isFirstPersonInputAllowed()) {
      return;
    }
    if (!this._activeTouches.has(event.pointerId)) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }
    const position = this._getRelativePointerPosition(event);
    this._activeTouches.set(event.pointerId, position);

    if (this._isPinching) {
      this._updatePinchMovement();
      return;
    }

    if (this._lookTouchId === event.pointerId && this.mouseDragOn) {
      const deltaX = position.x - this._lastPointerX;
      const deltaY = position.y - this._lastPointerY;
      this._pointerX += deltaX;
      this._pointerY += deltaY;
      this._lastPointerX = position.x;
      this._lastPointerY = position.y;
    }
  }

  private handleTouchEnd(event: PointerEvent): void {
    if (!this.isFirstPersonInputAllowed()) {
      return;
    }
    if (!this._activeTouches.has(event.pointerId)) {
      return;
    }

    this._activeTouches.delete(event.pointerId);

    if (this._isPinching && this._activeTouches.size < 2) {
      this._endPinch();
    }

    if (this._lookTouchId === event.pointerId) {
      this.mouseDragOn = false;
      this._lookTouchId = null;
      this._pointerX = 0;
      this._pointerY = 0;
    }

    if (!this._isPinching && this._activeTouches.size === 1) {
      this._assignLookTouch();
    }

    if (this._activeTouches.size === 0) {
      this.mouseDragOn = false;
    }
  }

  private _assignLookTouch(): void {
    const nextTouch = this._activeTouches.entries().next();
    if (nextTouch.done) {
      return;
    }

    const [pointerId, position] = nextTouch.value;
    this._lookTouchId = pointerId;
    this.mouseDragOn = true;
    this._lastPointerX = position.x;
    this._lastPointerY = position.y;
    this._pointerX = 0;
    this._pointerY = 0;
    this._dragVelocityX = 0;
    this._dragVelocityY = 0;
  }

  private _startPinch(): void {
    const distance = this._getPinchDistance();
    if (distance === null) {
      return;
    }

    this._isPinching = true;
    this._lastPinchDistance = distance;
    this._pendingPinchDistance = 0;
    this.mouseDragOn = false;
    this._lookTouchId = null;
    this._pointerX = 0;
    this._pointerY = 0;
    this._dragVelocityX = 0;
    this._dragVelocityY = 0;
  }

  private _updatePinchMovement(): void {
    const distance = this._getPinchDistance();
    if (distance === null || this._lastPinchDistance === null) {
      return;
    }

    const delta = this._lastPinchDistance - distance;
    this._pendingPinchDistance += delta;
    this._lastPinchDistance = distance;
  }

  private _endPinch(): void {
    this._isPinching = false;
    this._lastPinchDistance = null;
  }

  private _getPinchDistance(): number | null {
    if (this._activeTouches.size < 2) {
      return null;
    }

    const touches = Array.from(this._activeTouches.values());
    const a = touches[0];
    const b = touches[1];
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.hypot(dx, dy);
  }

  private _getRelativePointerPosition(event: PointerEvent): {
    x: number;
    y: number;
  } {
    if (
      this.domElement === null ||
      this.domElement === document.documentElement ||
      this.domElement === document.body
    ) {
      return { x: event.pageX, y: event.pageY };
    }

    return {
      x: event.pageX - this.domElement.offsetLeft,
      y: event.pageY - this.domElement.offsetTop,
    };
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (!this.isKeyboardSceneControlAllowed()) {
      return;
    }
    switch (event.code) {
      case "ArrowUp":
      case "KeyW":
        this._moveForward = true;
        break;

      case "ArrowLeft":
      case "KeyA":
        this._moveLeft = true;
        break;

      case "ArrowDown":
      case "KeyS":
        this._moveBackward = true;
        break;

      case "ArrowRight":
      case "KeyD":
        this._moveRight = true;
        break;

      case "KeyR":
        this._moveUp = true;
        break;
      case "KeyF":
        this._moveDown = true;
        break;
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    if (!this.isKeyboardSceneControlAllowed()) {
      return;
    }
    switch (event.code) {
      case "ArrowUp":
      case "KeyW":
        this._moveForward = false;
        break;

      case "ArrowLeft":
      case "KeyA":
        this._moveLeft = false;
        break;

      case "ArrowDown":
      case "KeyS":
        this._moveBackward = false;
        break;

      case "ArrowRight":
      case "KeyD":
        this._moveRight = false;
        break;

      case "KeyR":
        this._moveUp = false;
        break;
      case "KeyF":
        this._moveDown = false;
        break;
    }
  }

  private onContextMenu(event: MouseEvent): void {
    if (!this.isFirstPersonInputAllowed()) return;
    event.preventDefault();
  }
}
